/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState } from '@vaas/react-ui-kit';

import { Icon } from '@/components/Icon';

/**
 * In-shell panel shown when the signed-in user's role can't reach a route/feature.
 *
 * Built on the kit's `EmptyState`, which is what VEIS's `RequireRole` already renders for the same
 * situation - the two apps were telling a locked-out user the same thing through two different
 * panels. The bespoke `.no-access-*` rules are gone with it.
 *
 * `asChild` keeps the action a real anchor, so "back home" is still middle-clickable and shows a
 * URL on hover; the kit's `Button` only lends it the styling.
 */
export function NoAccess() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60svh] items-center justify-center p-8">
      <EmptyState
        icon={<Icon name="warn" size={24} className="text-[var(--color-warning)]" />}
        title={t('access.title')}
        description={t('access.description')}
        action={
          <Button asChild variant="outline">
            <Link to="/">
              <Icon name="home" size={16} /> {t('common.backHome')}
            </Link>
          </Button>
        }
      />
    </div>
  );
}
